import { Alert, Button, Card, Flex, Modal, Tag, Typography } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRecruiterAgreementVersions } from '@/api/recruiter';
import RecruiterTermsFeeSection from '@/components/recruiter/RecruiterTermsFeeSection';
import { useApiQuery } from '@/hooks/api';
import { queryKeys } from '@/shared/lib/queryKeys';
import { MODAL_WIDTH_MD, SPACE_MD } from '@/theme/buildAntdTheme';
import Avatar from '@/UI/Avatar/Avatar';
import Empty from '@/UI/Empty/Empty';
import Loader from '@/UI/Loader';
import ModalFooter from '@/UI/ModalFooter';
import { LabelTitle } from '@/UI/typography';
import { formatShortDate } from '@/utils/formatDate';

import type { AgreementWithCompany } from '@/features/recruiterContracts/types';
import type { CompanyAgreement } from '@/types/company';
import type { TagProps } from 'antd';
import type { TFunction } from 'i18next';

type ContractHistoryModalProps = {
  open: boolean;
  onClose: () => void;
  agreement: AgreementWithCompany | null;
  getCurrentVersionStatus: (agreement: CompanyAgreement) => string;
};

type ModalView = 'list' | 'detail';

function getVersionStatus(
  version: CompanyAgreement,
  t: TFunction,
): { label: string; color: TagProps['color'] } {
  if (version.accepted_date) {
    return {
      label: t('CONTRACT_STATUS_ACCEPTED'),
      color: 'success',
    };
  }
  if (version.declined_date) {
    return {
      label: t('CONTRACT_STATUS_DECLINED'),
      color: 'error',
    };
  }
  return {
    label: t('CONTRACT_STATUS_PENDING'),
    color: 'warning',
  };
}

function getVersionDateLabel(
  version: CompanyAgreement,
  language: string,
): string | null {
  if (version.accepted_date) {
    return formatShortDate(version.accepted_date, language);
  }
  if (version.declined_date) {
    return formatShortDate(version.declined_date, language);
  }
  return null;
}

export function buildContractAgreementProvider(agreement: CompanyAgreement) {
  return {
    ...agreement,
    recruiter_company_agreement: {
      ...agreement,
    },
    fee_currency: agreement.fee_currency,
  };
}

export function ContractHistoryModal({
  open,
  onClose,
  agreement,
  getCurrentVersionStatus,
}: ContractHistoryModalProps) {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<ModalView>('list');
  const [selectedVersion, setSelectedVersion] =
    useState<CompanyAgreement | null>(null);
  const fetchedAgreementIdRef = useRef<string | null>(null);

  const agreementId = agreement?.id;
  const { data: versions = [], isLoading: versionsLoading } = useApiQuery<
    CompanyAgreement[]
  >({
    queryKey: [
      ...queryKeys.recruiter.companyAgreement(agreementId ?? ''),
      'versions',
    ],
    queryFn: async () => {
      if (!agreementId) return [];
      return getRecruiterAgreementVersions(agreementId);
    },
    enabled: open && Boolean(agreementId),
  });

  const currentVersionNumber = agreement?.version ?? 0;

  const previousVersions = useMemo(() => {
    return versions
      .filter((v) => (v.version ?? 0) !== currentVersionNumber)
      .sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
  }, [versions, currentVersionNumber]);

  useEffect(() => {
    if (!open) {
      fetchedAgreementIdRef.current = null;
      return;
    }
    if (!agreement?.id) {
      return;
    }
    if (fetchedAgreementIdRef.current === agreement.id) {
      return;
    }
    fetchedAgreementIdRef.current = agreement.id;
    setView('list');
    setSelectedVersion(null);
  }, [agreement, open]);

  const handleClose = () => {
    setView('list');
    setSelectedVersion(null);
    onClose();
  };

  const handleSelectCurrent = () => {
    if (!agreement) return;
    setSelectedVersion(agreement);
    setView('detail');
  };

  const handleSelectPrevious = (version: CompanyAgreement) => {
    setSelectedVersion(version);
    setView('detail');
  };

  const handleBackToList = () => {
    setSelectedVersion(null);
    setView('list');
  };

  const companyName =
    agreement?.company_details?.name || agreement?.company_name || '';

  const renderHistoryList = () => (
    <>
      <Flex align="center" gap={12}>
        <LabelTitle>{t('CONTRACT_HISTORY')}</LabelTitle>
      </Flex>

      {versionsLoading ? (
        <Loader centered size="large" />
      ) : (
        <Flex
          vertical
          gap="small"
          style={{ maxHeight: 500, overflowY: 'auto' }}
        >
          <LabelTitle type="secondary">{t('CURRENT_VERSION')}</LabelTitle>
          <Card
            size="small"
            hoverable
            onClick={handleSelectCurrent}
            style={{
              border:
                view === 'list'
                  ? `2px solid var(--ant-color-primary)`
                  : undefined,
            }}
          >
            <Flex align="center" gap="small" wrap>
              <LabelTitle>
                {t('CONTRACT_VERSION')} {currentVersionNumber + 1}
              </LabelTitle>
              <Tag color="success">{t('ACTIVE')}</Tag>
              {agreement ? (
                <Tag color={getVersionStatus(agreement, t).color}>
                  {getVersionStatus(agreement, t).label}
                </Tag>
              ) : null}
            </Flex>
            {agreement ? (
              <Typography.Text type="secondary">
                {getCurrentVersionStatus(agreement)}
              </Typography.Text>
            ) : null}
          </Card>

          <LabelTitle type="secondary">
            {t('PREVIOUS_VERSIONS')} ({previousVersions.length})
          </LabelTitle>

          {previousVersions.length === 0 ? (
            <Empty size="small" description={t('NO_CONTRACT_HISTORY')} />
          ) : (
            previousVersions.map((version) => {
              const status = getVersionStatus(version, t);
              const dateLabel = getVersionDateLabel(version, i18n.language);
              return (
                <Card
                  key={`${version.version}-${version.id ?? 'snapshot'}`}
                  hoverable
                  size="small"
                  onClick={() => handleSelectPrevious(version)}
                >
                  <Flex vertical gap={0}>
                    <Flex align="center" gap="small" wrap>
                      <LabelTitle>
                        {t('CONTRACT_VERSION')} {(version.version ?? 0) + 1}
                      </LabelTitle>
                      <Tag color={status.color}>{status.label}</Tag>
                    </Flex>
                    {dateLabel ? (
                      <Typography.Text type="secondary">
                        {dateLabel}
                      </Typography.Text>
                    ) : null}
                  </Flex>
                </Card>
              );
            })
          )}
        </Flex>
      )}
    </>
  );

  const renderVersionDetail = () => {
    if (!selectedVersion || !agreement) return null;

    const isCurrent = (selectedVersion.version ?? 0) === currentVersionNumber;

    return (
      <Flex vertical gap={0}>
        {!isCurrent ? (
          <Alert type="info" showIcon={false} title={t('OLD_VERSION_INFO')} />
        ) : null}

        <Flex vertical gap={SPACE_MD}>
          <Avatar
            name={companyName}
            title={companyName}
            description={
              <Typography.Text type="secondary">
                {t('CONTRACT_VERSION')} {(selectedVersion.version ?? 0) + 1} —{' '}
                {getCurrentVersionStatus(selectedVersion)}
              </Typography.Text>
            }
          />
          <RecruiterTermsFeeSection
            agreementProvider={buildContractAgreementProvider(selectedVersion)}
          />
        </Flex>
      </Flex>
    );
  };

  const getTitle = () => {
    if (view === 'detail' && selectedVersion) {
      return t('CONTRACT_DETAILS_TITLE', { company: companyName });
    }
    return t('CONTRACT_HISTORY');
  };

  const getFooter = () => {
    if (view === 'detail') {
      return (
        <ModalFooter
          actions={
            <>
              <Button onClick={handleClose}>{t('CLOSE')}</Button>
              <Button type="primary" onClick={handleBackToList}>
                {t('BACK')}
              </Button>
            </>
          }
        />
      );
    }

    return (
      <ModalFooter
        actions={<Button onClick={handleClose}>{t('CLOSE')}</Button>}
      />
    );
  };

  return (
    <Modal
      open={open}
      title={getTitle()}
      onCancel={handleClose}
      footer={getFooter()}
      destroyOnHidden
      width={MODAL_WIDTH_MD}
      data-testid="contract-history-modal"
    >
      {view === 'list' ? renderHistoryList() : renderVersionDetail()}
    </Modal>
  );
}
